import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { AuthShell } from "./Login";
import { Loader2, Coins } from "lucide-react";
import GoogleAuthButton from "../components/app/GoogleAuthButton";
import TurnstileWidget from "../components/app/TurnstileWidget";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Register() {
  const { user, register, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreed) {
      setError(t("register.mustAgree"));
      return;
    }
    if (password.length < 6) {
      setError(t("register.pwShort"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("register.pwMismatch"));
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError(t("register.needBotCheck"));
      return;
    }
    setSubmitting(true);
    const res = await register(email.trim(), password, name.trim(), turnstileToken);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else nav("/", { replace: true });
  };

  return (
    <AuthShell title={t("register.title")} eyebrow={t("register.eyebrow")}>
      <div
        className="mb-6 -mt-2 flex items-center gap-2.5 px-3 py-2.5 rounded-md border border-white/10 bg-white/[0.03]"
        data-testid="signup-bonus-banner"
      >
        <Coins className="w-4 h-4 text-[#F5C542] flex-shrink-0" />
        <p className="text-[12.5px] text-white/80">
          <span className="font-medium text-white">{t("register.bonusStrong")}</span>
          <span className="text-white/50">{t("register.bonusRest")}</span>
        </p>
      </div>
      <GoogleAuthButton label={t("register.google")} />
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-white/10" />
        <div className="label-eyebrow">{t("login.or")}</div>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <label htmlFor="r-name" className="label-eyebrow block mb-2">{t("register.name")}</label>
          <input
            id="r-name"
            data-testid="register-name"
            type="text"
            className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("register.optional")}
          />
        </div>
        <div>
          <label htmlFor="r-email" className="label-eyebrow block mb-2">{t("register.email")}</label>
          <input
            id="r-email"
            data-testid="register-email"
            type="email"
            required
            autoComplete="email"
            className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="r-password" className="label-eyebrow block mb-2">{t("register.password")}</label>
          <input
            id="r-password"
            data-testid="register-password"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="r-password-confirm" className="label-eyebrow block mb-2">
            {t("register.confirmPassword")}
          </label>
          <input
            id="r-password-confirm"
            data-testid="register-password-confirm"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            className={`cmd-input w-full h-11 px-4 rounded-md text-[14px] ${
              confirmPassword && password !== confirmPassword ? "!border-[#ff9c85]/60" : ""
            }`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {confirmPassword && password !== confirmPassword && (
            <div
              className="mt-2 text-[12px] font-mono-p text-[#ff9c85]"
              data-testid="register-pw-mismatch"
            >
              {t("register.pwMismatch")}
            </div>
          )}
        </div>
        {error && (
          <div
            className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5"
            data-testid="register-error"
          >
            {error}
          </div>
        )}
        <label
          htmlFor="r-agree"
          className="flex items-start gap-3 text-[12px] text-white/70 cursor-pointer select-none"
          data-testid="register-agree-label"
        >
          <input
            id="r-agree"
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-white flex-shrink-0 cursor-pointer focus-ring"
            data-testid="register-agree-checkbox"
          />
          <span>
            {t("register.agreePrefix")}{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white text-white/85"
              data-testid="register-agree-terms"
            >
              {t("terms.link")}
            </Link>
            {" "}{t("register.agreeAnd")}{" "}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white text-white/85"
              data-testid="register-agree-privacy"
            >
              {t("cookie.privacyLink")}
            </Link>
            .
          </span>
        </label>
        {TURNSTILE_SITE_KEY && (
          <div className="flex justify-center" data-testid="turnstile-container">
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              theme="dark"
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken("")}
              onError={() => setTurnstileToken("")}
            />
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || !agreed || (TURNSTILE_SITE_KEY && !turnstileToken) || !password || password !== confirmPassword}
          className="btn-pill w-full h-11 justify-center focus-ring disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="register-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("register.submit")}
        </button>
      </form>
      <div className="mt-6 label-eyebrow text-center">
        {t("register.already")}{" "}
        <Link to="/login" className="underline hover:text-white" data-testid="link-to-login">
          {t("register.signIn")}
        </Link>
      </div>
    </AuthShell>
  );
}
